import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type FieldDefinition,
  type FieldOption,
  type ImportBatch,
  type PromoteResult,
  importSource,
  codeWords
} from '../lib/api'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { Dialog, Status, Toggletip, Picker } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/sweep.css'

// Tenant-defined fields. The three things this screen has to get across, in
// order of how expensive they are to learn late:
//
//   1. a custom field never reaches the risk score,
//   2. the type and the key of a field are fixed once values exist,
//   3. a field holding personal data has to say why and for how long.
//
// Each is stated once, where a reader would look for it, rather than in a note
// nobody reads at the top.

const ENTITIES = ['VENDOR', 'CONTACT', 'ENGAGEMENT', 'DOCUMENT']

const DATA_TYPES: { value: string; label: string }[] = [
  { value: 'TEXT', label: 'Short text, up to 512 characters' },
  { value: 'LONG_TEXT', label: 'Long text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Yes or no' },
  { value: 'DATE', label: 'Date' },
  { value: 'SINGLE_CHOICE', label: 'Single choice from a list' },
  { value: 'MULTI_CHOICE', label: 'Several choices from a list' },
  { value: 'EMAIL', label: 'Email address' },
  { value: 'PHONE', label: 'Phone number in E.164 form' },
  { value: 'URL', label: 'Web address' },
]

const TYPE_LABEL: Record<string, string> = {
  TEXT: 'Short text',
  LONG_TEXT: 'Long text',
  NUMBER: 'Number',
  BOOLEAN: 'Yes or no',
  DATE: 'Date',
  SINGLE_CHOICE: 'Single choice',
  MULTI_CHOICE: 'Several choices',
  EMAIL: 'Email address',
  PHONE: 'Phone number',
  URL: 'Web address',
}

const CHOICE_TYPES = ['SINGLE_CHOICE', 'MULTI_CHOICE']

// The four vendor attributes the API resolves a conditional requirement
// against. Offering a free text field here would let somebody name an
// attribute that never matches, and the field would then silently never be
// required.
const CONDITION_ATTRS = ['residency', 'constitution', 'country', 'status']

const RESERVED_PREFIXES = ['tprm_', 'foctta_']
const KEY_PATTERN = /^[a-z][a-z0-9_]{1,62}$/
const DEFAULT_SECTION = 'Additional information'

// The API reports a rejected definition as issues carrying a code rather than
// a form control name, so the code is what points the reader at the input.
const ISSUE_CONTROL: Record<string, string> = {
  ENTITY: 'entity',
  KEY_FORMAT: 'key',
  KEY_RESERVED: 'key',
  LABEL: 'label',
  OPTIONS: 'options',
  DATA_TYPE: 'type',
  PII_PURPOSE: 'pii-purpose',
  PII_RETENTION: 'pii-retention',
}

type FormError = { id: string; message: string }

function isChoice(dataType: string): boolean {
  return CHOICE_TYPES.includes(dataType)
}

function parseOptionLines(text: string): FieldOption[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .map((line) => {
      const bar = line.indexOf('|')
      if (bar < 0) return { value: line, label: line }
      const value = line.slice(0, bar).trim()
      const label = line.slice(bar + 1).trim()
      return { value, label: label || value }
    })
}

function optionLines(options: FieldOption[]): string {
  return options.map((o) => (o.label && o.label !== o.value ? o.value + ' | ' + o.label : o.value)).join('\n')
}

function buildRequiredWhen(attr: string, values: string): Record<string, unknown> | null {
  const field = attr.trim()
  const list = values
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '')
  const first = list[0]
  if (!field || first === undefined) return null
  return { [field]: list.length === 1 ? first : list }
}

function describeWhen(when: Record<string, unknown>): string {
  return Object.entries(when)
    .map(([field, want]) => {
      const list = Array.isArray(want) ? want.map((w) => String(w)) : [String(want)]
      return field + ' is ' + list.join(' or ')
    })
    .join(' and ')
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

// Server issues carry fieldKey rather than field, so the code is the only
// reliable route back to a control.
function issuesToErrors(err: ApiError, prefix: string): FormError[] {
  const out: FormError[] = []
  for (const issue of err.issues) {
    const control = ISSUE_CONTROL[issue.code]
    out.push({ id: control ? prefix + control : prefix + 'label', message: issue.detail })
  }
  if (out.length === 0) {
    out.push({
      id: err.status === 409 ? prefix + 'key' : prefix + 'label',
      message: err.message + ' ' + err.remedy,
    })
  }
  return out
}

// The console routes on the fragment, so an in-page link written as href="#id"
// would be read as a route, match none, and drop the reader on the portfolio
// with the half-filled form thrown away. Defeating the anchor with
// preventDefault only covers a plain left-click; a middle-click, a
// ctrl-click or "copy link address" all still yield the broken fragment. A
// button has no href to misuse.
function focusField(id: string): void {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ block: 'center' })
  el.focus()
}

function ErrorSummary({ id, errors, title }: { id: string; errors: FormError[]; title: string }): JSX.Element | null {
  if (errors.length === 0) return null
  return (
    <div className="form-errors" id={id} role="alert" tabIndex={-1}>
      <h3>{title}</h3>
      <ol>
        {errors.map((e, i) => (
          <li key={e.id + '-' + i}>
            <button type="button" className="linkish" onClick={() => focusField(e.id)}>
              {e.message}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

type Draft = {
  entity: string
  fieldKey: string
  label: string
  helpText: string
  dataType: string
  optionsText: string
  isRequired: boolean
  whenAttr: string
  whenValues: string
  isPii: boolean
  piiPurpose: string
  retentionDays: string
  section: string
  ordinal: string
}

function emptyDraft(entity: string): Draft {
  return {
    entity,
    fieldKey: '',
    label: '',
    helpText: '',
    dataType: 'TEXT',
    optionsText: '',
    isRequired: false,
    whenAttr: '',
    whenValues: '',
    isPii: false,
    piiPurpose: '',
    retentionDays: '',
    section: DEFAULT_SECTION,
    ordinal: '0',
  }
}

function draftErrors(d: Draft, prefix: string, takenKeys: string[]): FormError[] {
  const out: FormError[] = []
  const key = d.fieldKey.trim()

  if (key === '') {
    out.push({ id: prefix + 'key', message: 'Give the field a key, the name every stored value is filed under' })
  } else if (RESERVED_PREFIXES.some((p) => key.startsWith(p))) {
    out.push({ id: prefix + 'key', message: 'tprm_ and foctta_ are reserved for fields we ship, pick another prefix' })
  } else if (!KEY_PATTERN.test(key)) {
    out.push({ id: prefix + 'key', message: 'The key is lower case, digits and underscores, a letter first, 2 to 63 long' })
  } else if (takenKeys.includes(key)) {
    out.push({ id: prefix + 'key', message: 'The key ' + key + ' is already used on this entity, edit that field instead' })
  }

  if (d.label.trim() === '') {
    out.push({ id: prefix + 'label', message: 'Give the field a label, it is what a person reads on the form' })
  }

  if (isChoice(d.dataType)) {
    const options = parseOptionLines(d.optionsText)
    if (options.length < 2) {
      out.push({ id: prefix + 'options', message: 'A choice field needs at least two options, otherwise it is a constant' })
    }
    const seen = new Set<string>()
    for (const o of options) {
      if (o.value === '') {
        out.push({ id: prefix + 'options', message: 'An option value cannot be blank' })
        break
      }
      if (seen.has(o.value)) {
        out.push({ id: prefix + 'options', message: 'The option value ' + o.value + ' appears twice' })
        break
      }
      seen.add(o.value)
    }
  }

  if (d.isPii) {
    if (d.piiPurpose.trim() === '') {
      out.push({
        id: prefix + 'pii-purpose',
        message: 'Record why this personal data is collected, or the tenant cannot account for it',
      })
    }
    const days = Number(d.retentionDays)
    if (!Number.isFinite(days) || days <= 0) {
      out.push({
        id: prefix + 'pii-retention',
        message: 'Record how many days this personal data is kept, as a whole number above zero',
      })
    }
  }

  const halfCondition = (d.whenAttr !== '' || d.whenValues.trim() !== '') && buildRequiredWhen(d.whenAttr, d.whenValues) === null
  if (halfCondition) {
    out.push({
      id: prefix + 'when-values',
      message: 'Give both an attribute and at least one value, or the field is never required',
    })
  }

  return out
}

type EditState = {
  base: FieldDefinition
  label: string
  helpText: string
  optionsText: string
  section: string
  ordinal: string
  isActive: boolean
}

export function CustomFields(): JSX.Element {
  const [entity, setEntity] = useState('VENDOR')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [defs, setDefs] = useState<FieldDefinition[] | null>(null)
  const [listError, setListError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)

  const [draft, setDraft] = useState<Draft>(() => emptyDraft('VENDOR'))
  const [createErrors, setCreateErrors] = useState<FormError[]>([])
  // What was created, and its reference kept apart from the sentence so the
  // line stays short and the reference can be read on its own.
  const [createNote, setCreateNote] = useState<{ line: string; reference: string } | null>(null)
  const [creating, setCreating] = useState(false)

  const [edit, setEdit] = useState<EditState | null>(null)
  const [editErrors, setEditErrors] = useState<FormError[]>([])
  const [saving, setSaving] = useState(false)
  const [editNote, setEditNote] = useState<string | null>(null)
  const [refocus, setRefocus] = useState<string | null>(null)

  const createInvalid = useMemo(() => new Set(createErrors.map((e) => e.id)), [createErrors])
  const editInvalid = useMemo(() => new Set(editErrors.map((e) => e.id)), [editErrors])

  const [batches, setBatches] = useState<ImportBatch[] | null>(null)
  const [importId, setImportId] = useState('')
  const [columns, setColumns] = useState<{ unmappedColumns: string[]; note: string } | null>(null)
  const [columnsError, setColumnsError] = useState<unknown>(null)
  const [picked, setPicked] = useState<string[]>([])
  const [promoteEntity, setPromoteEntity] = useState('VENDOR')
  const [promoted, setPromoted] = useState<PromoteResult | null>(null)
  const [promoteError, setPromoteError] = useState<ApiError | null>(null)
  const [promoting, setPromoting] = useState(false)
  const [aboutScore, setAboutScore] = useState(false)

  const editPanel = useRef<HTMLHeadingElement>(null)

  const loadDefs = useCallback(() => {
    let live = true
    setLoading(true)
    api
      .fieldDefinitions(entity, includeInactive)
      .then((r) => {
        if (!live) return
        setDefs(r.definitions)
        setListError(null)
      })
      .catch((err) => {
        if (!live) return
        setDefs(null)
        setListError(err)
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [entity, includeInactive])

  useEffect(loadDefs, [loadDefs])

  useEffect(() => {
    api
      .imports()
      .then((r) => setBatches(r.imports))
      .catch(() => setBatches([]))
  }, [])

  useEffect(() => {
    if (!importId) {
      setColumns(null)
      setPicked([])
      return
    }
    let live = true
    setColumns(null)
    setColumnsError(null)
    setPromoted(null)
    setPicked([])
    api
      .unmappedColumns(importId)
      .then((r) => {
        if (live) setColumns(r)
      })
      .catch((err) => {
        if (live) setColumnsError(err)
      })
    return () => {
      live = false
    }
  }, [importId])

  // Focus follows the reader into the edit panel because they asked for it by
  // pressing Edit, and returns to that button when they leave.
  const editingId = edit?.base.id
  useEffect(() => {
    if (editingId) editPanel.current?.focus()
  }, [editingId])

  // A rejected submit puts the reader on the summary of what was wrong. It has
  // to wait for the render that creates the summary: calling focus from inside
  // the submit handler reaches for an element React has not written yet, so the
  // first failed attempt silently leaves focus where it was.
  useEffect(() => {
    if (createErrors.length > 0) document.getElementById('cf-errors')?.focus()
  }, [createErrors])

  useEffect(() => {
    if (editErrors.length > 0) document.getElementById('ed-errors')?.focus()
  }, [editErrors])

  // A save reloads the list, which replaces the row the reader came from, so
  // the button is refocused only once the new rows are on screen. Focusing it
  // before then lands on an element React is about to discard, and the reader
  // is dropped back on the document body with no idea where they were.
  useEffect(() => {
    if (!refocus || loading) return
    const target = document.getElementById('edit-' + refocus) ?? document.getElementById('cf-list-heading')
    target?.focus()
    setRefocus(null)
  }, [refocus, loading])

  const canWrite = can('programme.write')

  const grouped = useMemo(() => {
    const map = new Map<string, FieldDefinition[]>()
    for (const d of defs ?? []) {
      const section = d.section || DEFAULT_SECTION
      const list = map.get(section)
      if (list) list.push(d)
      else map.set(section, [d])
    }
    return [...map.entries()]
  }, [defs])

  const takenKeys = useMemo(() => (draft.entity === entity ? (defs ?? []).map((d) => d.fieldKey) : []), [defs, draft.entity, entity])

  const piiCount = (defs ?? []).filter((d) => d.isPii).length
  const inactiveCount = (defs ?? []).filter((d) => !d.isActive).length

  function openEdit(d: FieldDefinition) {
    setEditErrors([])
    setEditNote(null)
    setEdit({
      base: d,
      label: d.label,
      helpText: d.helpText,
      optionsText: optionLines(d.options ?? []),
      section: d.section,
      ordinal: String(d.ordinal),
      isActive: d.isActive,
    })
  }

  function closeEdit(id: string) {
    setEdit(null)
    setEditErrors([])
    document.getElementById('edit-' + id)?.focus()
  }

  async function submitCreate(e: FormEvent) {
    e.preventDefault()
    setCreateNote(null)
    const local = draftErrors(draft, 'cf-', takenKeys)
    if (local.length > 0) {
      setCreateErrors(local)
      return
    }
    setCreateErrors([])
    setCreating(true)
    try {
      const when = buildRequiredWhen(draft.whenAttr, draft.whenValues)
      const days = Number(draft.retentionDays)
      const body: Partial<FieldDefinition> = {
        entity: draft.entity,
        fieldKey: draft.fieldKey.trim(),
        label: draft.label.trim(),
        helpText: draft.helpText.trim(),
        dataType: draft.dataType,
        options: isChoice(draft.dataType) ? parseOptionLines(draft.optionsText) : [],
        isRequired: draft.isRequired,
        requiredWhen: when,
        isPii: draft.isPii,
        piiPurpose: draft.isPii ? draft.piiPurpose.trim() : '',
        retentionDays: draft.isPii && Number.isFinite(days) ? days : null,
        section: draft.section.trim() || DEFAULT_SECTION,
        ordinal: Number(draft.ordinal) || 0,
        isActive: true,
      }
      const created = await api.createFieldDefinition(body)
      setCreateNote({
        line: draft.label.trim() + ' is defined on ' + draft.entity.toLowerCase() + ' records as ' + draft.fieldKey.trim(),
        reference: created.id,
      })
      const keepEntity = draft.entity
      setDraft(emptyDraft(keepEntity))
      if (keepEntity === entity) loadDefs()
    } catch (err) {
      const apiErr = asApiError(err)
      setCreateErrors(issuesToErrors(apiErr, 'cf-'))
    } finally {
      setCreating(false)
    }
  }

  async function submitEdit(e: FormEvent) {
    e.preventDefault()
    if (!edit) return
    const local: FormError[] = []
    if (edit.label.trim() === '') {
      local.push({ id: 'ed-label', message: 'A label is what a person reads on the form and cannot be blank' })
    }
    if (isChoice(edit.base.dataType)) {
      const options = parseOptionLines(edit.optionsText)
      if (options.length < 2) {
        local.push({ id: 'ed-options', message: 'A choice field needs at least two options, otherwise it is a constant' })
      }
      const seen = new Set<string>()
      for (const o of options) {
        if (o.value === '' || seen.has(o.value)) {
          local.push({ id: 'ed-options', message: 'Each option needs a value, and no value may appear twice' })
          break
        }
        seen.add(o.value)
      }
    }
    if (local.length > 0) {
      setEditErrors(local)
      return
    }
    setEditErrors([])
    setSaving(true)
    try {
      // The whole definition goes back, not only what this form edits. The
      // update statement writes every column it is given, so omitting the
      // personal data flag or the conditional rule would quietly clear them.
      await api.updateFieldDefinition(edit.base.id, {
        entity: edit.base.entity,
        fieldKey: edit.base.fieldKey,
        dataType: edit.base.dataType,
        label: edit.label.trim(),
        helpText: edit.helpText.trim(),
        options: isChoice(edit.base.dataType) ? parseOptionLines(edit.optionsText) : [],
        isRequired: edit.base.isRequired,
        requiredWhen: edit.base.requiredWhen ?? null,
        isPii: edit.base.isPii,
        piiPurpose: edit.base.piiPurpose ?? '',
        retentionDays: edit.base.retentionDays ?? null,
        section: edit.section.trim() || DEFAULT_SECTION,
        ordinal: Number(edit.ordinal) || 0,
        isActive: edit.isActive,
      })
      setEditNote(edit.label.trim() + ' saved')
      setEdit(null)
      setEditErrors([])
      setRefocus(edit.base.id)
      loadDefs()
    } catch (err) {
      setEditErrors(issuesToErrors(asApiError(err), 'ed-'))
    } finally {
      setSaving(false)
    }
  }

  async function submitPromote(e: FormEvent) {
    e.preventDefault()
    if (!importId || picked.length === 0) return
    setPromoting(true)
    setPromoteError(null)
    setPromoted(null)
    try {
      const result = await api.promoteColumns(importId, picked, promoteEntity)
      setPromoted(result)
      setPicked([])
      if (promoteEntity === entity) loadDefs()
    } catch (err) {
      setPromoteError(asApiError(err))
    } finally {
      setPromoting(false)
    }
  }

  const columns8: Column<FieldDefinition>[] = [
    {
      key: 'field',
      header: 'Field',
      sortValue: (d) => d.label,
      render: (d) => (
        <>
          <span className="cell-primary">{d.label}</span>
          <span className="cell-sub mono">{d.fieldKey}</span>
          {d.helpText && <span className="cell-sub">{d.helpText}</span>}
        </>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortValue: (d) => d.dataType,
      render: (d) => (
        <>
          <span>{TYPE_LABEL[d.dataType] ?? d.dataType}</span>
          {isChoice(d.dataType) && (
            <span className="cell-sub">{(d.options ?? []).length} options</span>
          )}
        </>
      ),
    },
    {
      key: 'required',
      header: 'Required',
      sortValue: (d) => (d.isRequired ? 2 : d.requiredWhen ? 1 : 0),
      render: (d) => {
        if (d.isRequired) return <Status tone="info">Always required</Status>
        if (d.requiredWhen && Object.keys(d.requiredWhen).length > 0) {
          return (
            <>
              <Status tone="neutral">Conditional</Status>
              <span className="cell-sub">{describeWhen(d.requiredWhen)}</span>
            </>
          )
        }
        return <span className="muted">Optional</span>
      },
    },
    {
      key: 'pii',
      header: 'Personal data',
      sortValue: (d) => (d.isPii ? 1 : 0),
      render: (d) =>
        d.isPii ? (
          <>
            <Status tone="warn">Personal data</Status>
            <span className="cell-sub">
              {d.piiPurpose || 'no purpose recorded'}
              {d.retentionDays ? ', kept ' + d.retentionDays + ' days' : ''}
            </span>
          </>
        ) : (
          <span className="muted">None declared</span>
        ),
    },
    {
      key: 'origin',
      header: 'Came from',
      sortValue: (d) => d.origin,
      render: (d) =>
        d.origin === 'IMPORT_PROMOTION' ? (
          <>
            <span>Promoted from an import</span>
            <span className="cell-sub mono">{d.originRef || 'no import reference recorded'}</span>
          </>
        ) : d.origin === 'MANUAL' ? (
          <span>Defined here</span>
        ) : (
          <span className="mono">{d.origin}</span>
        ),
    },
    {
      key: 'ordinal',
      header: 'Order',
      numeric: true,
      sortValue: (d) => d.ordinal,
      render: (d) => <span className="mono">{d.ordinal}</span>,
    },
    {
      key: 'active',
      header: 'Status',
      sortValue: (d) => (d.isActive ? 1 : 0),
      render: (d) =>
        d.isActive ? <Status tone="ok">Active</Status> : <Status tone="plain">Retired</Status>,
    },
    {
      key: 'edit',
      header: 'Edit',
      render: (d) => (
        <button
          type="button"
          id={'edit-' + d.id}
          onClick={() => openEdit(d)}
          disabled={!canWrite}
          title={canWrite ? undefined : refusalReason('programme.write')}
        >
          Edit<span className="vh"> {d.label}</span>
        </button>
      ),
    },
  ]

  const selectedBatch = (batches ?? []).find((b) => b.id === importId) ?? null
  const unmapped = columns?.unmappedColumns ?? []

  return (
    <>
      <div className="page-head">
        <h1>Custom fields</h1>
      </div>

      {/* The rule stays on the page as one line; the reasoning behind it runs
          past what a toggletip holds, so it opens in a drawer. */}
      <div className="note sw-row" style={{ marginBottom: 16 }}>
        <strong style={{ margin: 0 }}>A custom field describes a vendor, it never moves the risk band</strong>
        <button type="button" className="linkish" aria-haspopup="dialog" onClick={() => setAboutScore(true)}>
          Why
        </button>
      </div>
      <Dialog shape="drawer" kind="record" open={aboutScore} onClose={() => setAboutScore(false)} title="Why a custom field never moves the risk band">
        <p>
          A score is reproducible only because every input to it is pinned by an immutable version, and these
          definitions carry no version: their values are edited in place. A gate reading one would mean last
          quarter's board pack quietly stops reproducing. Anything that must affect risk becomes a questionnaire
          question instead, where it is versioned, weighted and citable.
        </p>
      </Dialog>

      <section className="panel" aria-labelledby="cf-list-heading">
        <div className="filter-group" style={{ marginBottom: 10 }}>
          <span className="label" id="cf-entity-filter-label">
            Entity
          </span>
          <div className="chiprow" role="group" aria-labelledby="cf-entity-filter-label">
            {ENTITIES.map((e) => (
              <button
                key={e}
                type="button"
                className="chip-toggle"
                aria-pressed={entity === e}
                onClick={() => setEntity(e)}
              >
                {codeWords(e)}
              </button>
            ))}
            <button
              type="button"
              className="chip-toggle"
              aria-pressed={includeInactive}
              onClick={() => setIncludeInactive((v) => !v)}
            >
              Include retired fields
            </button>
          </div>
        </div>

        <div className="panel-head" style={{ margin: '0 -16px', borderTop: '1px solid var(--line)' }}>
          {/* Focusable only so that a reader returning from the edit panel has
              somewhere to land when the row they came from is no longer listed,
              which is what retiring a field does. */}
          <h2 id="cf-list-heading" tabIndex={-1} style={{ margin: 0 }}>
            Fields on {entity.toLowerCase()} records
          </h2>
        </div>

        {loading && !defs && <Loading what="the custom field definitions" />}
        {listError != null && <ErrorBox error={listError} what="The custom field definitions" />}

        {defs && defs.length === 0 && (
          <div className="empty">
            <strong>No custom field is defined on {entity.toLowerCase()} records</strong>
            Add one below, or promote the columns an import could not place{' '}
            <Toggletip label="Where retired fields are">
              Retired fields are hidden unless you ask for them with the toggle above.
            </Toggletip>
          </div>
        )}

        {grouped.map(([section, rows]) => (
          <div key={section} style={{ margin: '0 -16px' }}>
            <div className="section-head">
              {section} <span className="mono">{rows.length} field{rows.length === 1 ? '' : 's'}</span>
            </div>
            <DataTable
              caption={
                'Custom fields in the ' +
                section +
                ' section, with the key values are stored under, the data type, whether an answer is required, whether the field holds personal data, where the definition came from and the order it appears in.'
              }
              columns={columns8}
              rows={rows}
              rowKey={(d) => d.id}
              defaultSort={{ key: 'ordinal', dir: 'asc' }}
              empty={{ title: 'This section is empty', detail: 'Nothing is filed under ' + section }}
            />
          </div>
        ))}

        {defs && defs.length > 0 && (
          <ListCount shown={defs.length} noun="fields">
            {' '}in {grouped.length} {grouped.length === 1 ? 'section' : 'sections'}, {piiCount} holding personal data
            {includeInactive ? ', ' + inactiveCount + ' retired' : ''}
          </ListCount>
        )}

        {editNote && (
          <p className="summary-line" role="status" style={{ marginTop: 10 }}>
            {editNote}
          </p>
        )}
      </section>

      {edit && (
        <section className="panel" aria-labelledby="ed-heading">
          <h2 id="ed-heading" ref={editPanel} tabIndex={-1}>
            Edit {edit.base.label}
          </h2>

          <ErrorSummary id="ed-errors" errors={editErrors} title="This field was not saved" />

          <dl className="kv" style={{ marginBottom: 12 }}>
            <dt>Field key</dt>
            <dd>
              <span className="mono">{edit.base.fieldKey}</span>
            </dd>
            <dt>Data type</dt>
            <dd>
              {TYPE_LABEL[edit.base.dataType] ?? edit.base.dataType}
            </dd>
            <dt>Required</dt>
            <dd>
              {edit.base.isRequired
                ? 'Always required'
                : edit.base.requiredWhen && Object.keys(edit.base.requiredWhen).length > 0
                  ? 'Required when ' + describeWhen(edit.base.requiredWhen)
                  : 'Optional'}
            </dd>
            <dt>Personal data</dt>
            <dd>
              {edit.base.isPii
                ? 'Yes. ' +
                  (edit.base.piiPurpose || 'no purpose recorded') +
                  (edit.base.retentionDays ? ', kept ' + edit.base.retentionDays + ' days' : '')
                : 'None declared'}
            </dd>
          </dl>

          <form onSubmit={submitEdit}>
            <div className="form-grid">
              <div className="field field-wide">
                <label htmlFor="ed-label">Label</label>
                <input
                  id="ed-label"
                  aria-invalid={editInvalid.has('ed-label') || undefined}
                  type="text"
                  value={edit.label}
                  onChange={(ev) => setEdit({ ...edit, label: ev.target.value })}
                />
              </div>

              <div className="field field-wide">
                <label htmlFor="ed-help">Help text</label>
                <input
                  id="ed-help"
                  type="text"
                  value={edit.helpText}
                  onChange={(ev) => setEdit({ ...edit, helpText: ev.target.value })}
                />
              </div>

              {isChoice(edit.base.dataType) && (
                <div className="field field-wide">
                  <label htmlFor="ed-options">Options, one per line</label>
                  <div className="sw-row">
                    <p id="ed-options-help" className="hint">
                      Each as value, or value | label, the label being what a person reads
                    </p>
                    <Toggletip label="What removing an option does">
                      Removing an option does not remove it from records that already hold it.
                    </Toggletip>
                  </div>
                  <textarea
                    id="ed-options"
                    aria-invalid={editInvalid.has('ed-options') || undefined}
                    rows={4}
                    aria-describedby="ed-options-help"
                    value={edit.optionsText}
                    onChange={(ev) => setEdit({ ...edit, optionsText: ev.target.value })}
                  />
                </div>
              )}

              <div className="field">
                <label htmlFor="ed-section">Section</label>
                <input
                  id="ed-section"
                  type="text"
                  value={edit.section}
                  onChange={(ev) => setEdit({ ...edit, section: ev.target.value })}
                />
              </div>

              <div className="field">
                <label htmlFor="ed-ordinal">Position in the section</label>
                <input
                  id="ed-ordinal"
                  type="number"
                  step="1"
                  value={edit.ordinal}
                  onChange={(ev) => setEdit({ ...edit, ordinal: ev.target.value })}
                />
              </div>
            </div>

            <div className="check" style={{ marginBottom: 12 }}>
              <input
                id="ed-active"
                type="checkbox"
                checked={edit.isActive}
                aria-describedby="ed-active-why"
                onChange={(ev) => setEdit({ ...edit, isActive: ev.target.checked })}
              />
              <div>
                <span className="sw-title">
                  <label htmlFor="ed-active">Active</label>
                  <Toggletip label="Why there is no delete">
                    There is no delete, because a field somebody answered is part of the record.
                  </Toggletip>
                </span>
                <p className="check-why" id="ed-active-why">
                  Retiring stops it being offered on new records and keeps every stored value
                </p>
              </div>
            </div>

            <div className="chiprow">
              <button
                type="submit"
                className="primary"
                disabled={saving || !canWrite}
                title={canWrite ? undefined : refusalReason('programme.write')}
                data-action="tprm.custom-fields.a02"
              >
                {saving ? 'Saving' : 'Save changes'}
              </button>
              <button type="button" onClick={() => closeEdit(edit.base.id)}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="panel" aria-labelledby="cf-create-heading">
        <h2 id="cf-create-heading">Add a field</h2>

        {!canWrite && (
          <p className="note warn sw-row" style={{ marginBottom: 12 }}>
            <Status tone="neutral">Read only</Status>
            <span>Your roles read but do not write</span>
            <ReasonTip label="Why you cannot add a field" text={refusalReason('programme.write')} />
          </p>
        )}

        <ErrorSummary id="cf-errors" errors={createErrors} title="This field was not created" />

        {createNote && (
          <div className="note" role="status" style={{ marginBottom: 12 }}>
            <strong>Field created</strong>
            <span className="sw-row">
              <span>{createNote.line}</span>
              <span className="mono muted">Reference {createNote.reference}</span>
            </span>
          </div>
        )}

        <form onSubmit={submitCreate}>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="cf-entity">Attaches to</label>
              <select
                id="cf-entity"
                aria-invalid={createInvalid.has('cf-entity') || undefined}
                value={draft.entity}
                onChange={(ev) => setDraft({ ...draft, entity: ev.target.value })}
              >
                {ENTITIES.map((e) => (
                  <option key={e} value={e}>
                    {codeWords(e)}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="cf-key">Field key</label>
              <div className="sw-row">
                <p id="cf-key-help" className="hint">
                  Lower case, digits and underscores, fixed once the field holds values
                </p>
                <Toggletip label="Reserved prefixes">
                  tprm_ and foctta_ are reserved for fields we ship, so a field you add here can never collide with one
                  the product gains later.
                </Toggletip>
              </div>
              <input
                id="cf-key"
                aria-invalid={createInvalid.has('cf-key') || undefined}
                type="text"
                className="mono"
                spellCheck={false}
                aria-describedby="cf-key-help"
                placeholder="parent_group_code"
                value={draft.fieldKey}
                onChange={(ev) => setDraft({ ...draft, fieldKey: ev.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="cf-label">Label</label>
              <input
                id="cf-label"
                aria-invalid={createInvalid.has('cf-label') || undefined}
                type="text"
                placeholder="Parent group code"
                value={draft.label}
                onChange={(ev) => setDraft({ ...draft, label: ev.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="cf-type">Data type</label>
              <p id="cf-type-help" className="hint">
                Choose carefully, the type is fixed once anyone fills the field in
              </p>
              <select
                id="cf-type"
                aria-invalid={createInvalid.has('cf-type') || undefined}
                aria-describedby="cf-type-help"
                value={draft.dataType}
                onChange={(ev) => setDraft({ ...draft, dataType: ev.target.value })}
              >
                {DATA_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field field-wide">
              <label htmlFor="cf-help">Help text</label>
              <input
                id="cf-help"
                type="text"
                placeholder="The group code used in the consolidated spend report"
                value={draft.helpText}
                onChange={(ev) => setDraft({ ...draft, helpText: ev.target.value })}
              />
            </div>

            {isChoice(draft.dataType) && (
              <div className="field field-wide">
                <label htmlFor="cf-options">Options, one per line</label>
                <div className="sw-row">
                  <p id="cf-options-help" className="hint">
                    At least two, each as value or value | label: PREPAID | Paid before delivery
                  </p>
                  <Toggletip label="Value and label">
                    The value is what is stored, and the label after the vertical bar is what a person reads.
                  </Toggletip>
                </div>
                <textarea
                  id="cf-options"
                  aria-invalid={createInvalid.has('cf-options') || undefined}
                  rows={4}
                  aria-describedby="cf-options-help"
                  value={draft.optionsText}
                  onChange={(ev) => setDraft({ ...draft, optionsText: ev.target.value })}
                />
              </div>
            )}

            <div className="field">
              <label htmlFor="cf-section">Section</label>
              <input
                id="cf-section"
                type="text"
                value={draft.section}
                onChange={(ev) => setDraft({ ...draft, section: ev.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="cf-ordinal">Position in the section</label>
              <input
                id="cf-ordinal"
                type="number"
                step="1"
                value={draft.ordinal}
                onChange={(ev) => setDraft({ ...draft, ordinal: ev.target.value })}
              />
            </div>
          </div>

          <div className="check" style={{ marginBottom: 10 }}>
            <input
              id="cf-required"
              type="checkbox"
              checked={draft.isRequired}
              aria-describedby="cf-required-why"
              onChange={(ev) => setDraft({ ...draft, isRequired: ev.target.checked })}
            />
            <div>
              <span className="sw-title">
                <label htmlFor="cf-required">Always required</label>
                <Toggletip label="When not to tick this">
                  If it only applies to some records, leave this clear and set the condition below instead, or the
                  records that cannot answer fill with placeholder values.
                </Toggletip>
              </span>
              <p className="check-why" id="cf-required-why">
                Refused empty on every record of this entity
              </p>
            </div>
          </div>

          <fieldset className="fieldset">
            <legend>Required only in some cases</legend>
            <div className="form-grid" style={{ marginBottom: 0 }}>
              <div className="field">
                <label htmlFor="cf-when-attr">When this attribute</label>
                <select
                  id="cf-when-attr"
                  value={draft.whenAttr}
                  onChange={(ev) => setDraft({ ...draft, whenAttr: ev.target.value })}
                >
                  <option value="">Not conditional</option>
                  {CONDITION_ATTRS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="cf-when-values">Is one of these values</label>
                <p id="cf-when-help" className="hint">
                  Comma separated and matched in any case, for example NON_RESIDENT
                </p>
                <input
                  id="cf-when-values"
                  aria-invalid={createInvalid.has('cf-when-values') || undefined}
                  type="text"
                  aria-describedby="cf-when-help"
                  value={draft.whenValues}
                  onChange={(ev) => setDraft({ ...draft, whenValues: ev.target.value })}
                />
              </div>
            </div>
          </fieldset>

          <div className="check check-pii" style={{ margin: '12px 0' }}>
            <input
              id="cf-pii"
              type="checkbox"
              checked={draft.isPii}
              aria-describedby="cf-pii-why"
              onChange={(ev) => setDraft({ ...draft, isPii: ev.target.checked })}
            />
            <div>
              <span className="sw-title">
                <label htmlFor="cf-pii">This field holds personal data</label>
                <Toggletip label="What leaving it clear risks">
                  Leave it clear on a field that does hold personal data and the erasure runs past it, which is the
                  failure nobody notices until a data principal asks.
                </Toggletip>
              </span>
              <p className="check-why" id="cf-pii-why">
                Ticked, the field joins the set an erasure request clears
              </p>
            </div>
          </div>

          {draft.isPii && (
            <fieldset className="fieldset">
              <legend>What this personal data is for</legend>
              <div className="form-grid" style={{ marginBottom: 0 }}>
                <div className="field field-wide">
                  <label htmlFor="cf-pii-purpose">Purpose</label>
                  <input
                    id="cf-pii-purpose"
                    aria-invalid={createInvalid.has('cf-pii-purpose') || undefined}
                    type="text"
                    placeholder="Reaching the vendor's named escalation contact during an incident"
                    value={draft.piiPurpose}
                    onChange={(ev) => setDraft({ ...draft, piiPurpose: ev.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="cf-pii-retention">Retention, in days</label>
                  <input
                    id="cf-pii-retention"
                    aria-invalid={createInvalid.has('cf-pii-retention') || undefined}
                    type="number"
                    min="1"
                    step="1"
                    placeholder="1095"
                    value={draft.retentionDays}
                    onChange={(ev) => setDraft({ ...draft, retentionDays: ev.target.value })}
                  />
                </div>
              </div>
            </fieldset>
          )}

          <div className="chiprow" style={{ marginTop: 12 }}>
            <button
              type="submit"
              className="primary"
              disabled={creating || !canWrite}
              title={canWrite ? undefined : refusalReason('programme.write')}
              data-action="tprm.custom-fields.a01"
            >
              {creating ? 'Defining' : 'Define this field'}
            </button>
            <button type="button" onClick={() => setDraft(emptyDraft(draft.entity))}>
              Clear the form
            </button>
          </div>
        </form>
      </section>

      <section className="panel" aria-labelledby="cf-promote-heading">
        <h2 id="cf-promote-heading">Promote columns from an import</h2>

        <p>The columns your export carried that no mapping profile knows about</p>

        <div className="note warn" style={{ marginBottom: 12 }}>
          <strong>A promoted field arrives as short text and declares no personal data</strong>
          Set both before anyone fills it in, as the type is fixed once values exist{' '}
          <Toggletip label="Why neither is guessed">
            Guessing that a column holds personal data either way is worse than making somebody say. Once values are
            stored there is no way back but retiring the field and starting again.
          </Toggletip>
        </div>

        {batches !== null && batches.length === 0 && (
          <div className="empty">
            <strong>No file has been imported in this tenant</strong>
            Import one from <a href="#/import">Import vendors</a>; columns it could not place show here
          </div>
        )}

        {batches !== null && batches.length > 0 && (
          <form onSubmit={submitPromote}>
            <div className="form-grid">
              <div className="field field-wide">
                <label htmlFor="cf-import">Import</label>
                {/* One row per file ever staged, so it grows with every import. */}
                <Picker
                  bare
                  id="cf-import"
                  label="Import"
                  value={importId}
                  options={batches.map((b) => ({
                    value: b.id,
                    label:
                      b.fileName +
                      (' (' + importSource(b) + ', ' + b.entity.toLowerCase() + ', ' + b.rowsTotal + ' rows') +
                      (b.createdAt ? ', ' + shortDate(b.createdAt) : '') +
                      ')',
                  }))}
                  placeholder="Choose an import: search by file name"
                  noun="import"
                  missing="An import not in this list"
                  onChange={setImportId}
                />
              </div>

              <div className="field">
                <label htmlFor="cf-promote-entity">Promote onto</label>
                <select
                  id="cf-promote-entity"
                  value={promoteEntity}
                  onChange={(ev) => setPromoteEntity(ev.target.value)}
                >
                  {ENTITIES.map((e) => (
                    <option key={e} value={e}>
                      {codeWords(e)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {columnsError != null && <ErrorBox error={columnsError} what="The unmapped columns" />}

            {importId && !columns && columnsError == null && <Loading what="the columns this import could not place" />}

            {columns && unmapped.length === 0 && (
              <div className="empty">
                <strong>Every column in this file was mapped</strong>
                Nothing to promote from {selectedBatch ? selectedBatch.fileName : 'this import'}
              </div>
            )}

            {columns && unmapped.length > 0 && (
              <>
                <fieldset className="fieldset">
                  <legend>Columns this import could not place ({unmapped.length})</legend>
                  {/* The API's note on these columns is kept whole behind the (i). */}
                  <p className="hint sw-row" style={{ marginBottom: 8 }}>
                    <span>Promoted columns stay listed, a second promotion is skipped as the key is taken</span>
                    <ReasonTip label="About these columns" text={columns.note} />
                  </p>
                  <div className="colgrid">
                    {unmapped.map((col, i) => {
                      const id = 'col-' + i + '-' + col.replace(/[^A-Za-z0-9]+/g, '-')
                      return (
                        <div className="check" key={col}>
                          <input
                            id={id}
                            type="checkbox"
                            checked={picked.includes(col)}
                            onChange={(ev) =>
                              setPicked((prev) => (ev.target.checked ? [...prev, col] : prev.filter((c) => c !== col)))
                            }
                          />
                          <label htmlFor={id} className="mono">
                            {col}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </fieldset>

                <div className="chiprow" style={{ marginTop: 12 }}>
                  <button
                    type="submit"
                    className="primary"
                    disabled={promoting || picked.length === 0 || !canWrite}
                    title={canWrite ? undefined : refusalReason('programme.write')}
                    data-action="tprm.custom-fields.a03"
                  >
                    {promoting
                      ? 'Promoting'
                      : picked.length === 0
                        ? 'Select a column to promote'
                        : 'Promote ' + picked.length + ' column' + (picked.length === 1 ? '' : 's')}
                  </button>
                  {picked.length > 0 && (
                    <button type="button" onClick={() => setPicked([])}>
                      Clear the selection
                    </button>
                  )}
                </div>
              </>
            )}
          </form>
        )}

        {promoteError && (
          <div className="error-box" role="alert" style={{ marginTop: 12 }}>
            <h3>Nothing was promoted ({promoteError.code})</h3>
            <p>{promoteError.message}</p>
            <p className="muted">{promoteError.remedy}</p>
          </div>
        )}

        {promoted && (
          <div style={{ marginTop: 12 }} role="status">
            {/* The API's note on what a promoted field still needs is kept whole
                behind the (i) beside the counts it follows. */}
            <p className="summary-line sw-row">
              <span>
                <strong>{promoted.created.length}</strong> created, <strong>{promoted.skipped.length}</strong> skipped
              </span>
              <ReasonTip label="What a promoted field still needs" text={promoted.note} />
            </p>

            {promoted.created.length > 0 && (
              <ul className="result-list">
                {promoted.created.map((c) => (
                  <li key={c.id}>
                    <span className="mono">{c.column}</span> became the field{' '}
                    <span className="mono">{c.fieldKey}</span>
                  </li>
                ))}
              </ul>
            )}

            {promoted.skipped.length > 0 && (
              <ul className="result-list skipped">
                {promoted.skipped.map((s) => (
                  <li key={s.column}>
                    <span className="mono">{s.column}</span> was skipped: {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    </>
  )
}
